import { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box,
  Typography, TextField, Button, Alert, CircularProgress,
  Chip, Divider, InputAdornment, Paper, Grid,
} from "@mui/material";

import CloseIcon         from "@mui/icons-material/Close";
import PaymentIcon       from "@mui/icons-material/Payment";
import TwoWheelerIcon    from "@mui/icons-material/TwoWheeler";
import PersonIcon        from "@mui/icons-material/Person";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import PhoneIcon         from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptIcon       from "@mui/icons-material/Receipt";
import EditIcon          from "@mui/icons-material/Edit";
import NotesIcon         from "@mui/icons-material/Notes";

import api from "../service/api";
import { DueSummary, money } from "./ExchangeDuePaymentModal";

// ─── tiny helpers ─────────────────────────────────────────────────────────────
const Label = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ color: "#888", fontWeight: 700, fontSize: "0.62rem", letterSpacing: 0.6, textTransform: "uppercase" }}
  >
    {children}
  </Typography>
);
const Val = ({ children, color }) => (
  <Typography variant="body2" fontWeight={700} color={color || "#111"} mt={0.2}>
    {children || "—"}
  </Typography>
);

// ─── COMPONENT ────────────────────────────────────────────────────────────────
const UpdateExchangePaymentModal = ({ open, onClose, bike, onSuccess }) => {

  const [saleDetail,   setSaleDetail]   = useState(null);
  const [saleId,       setSaleId]       = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError,   setFetchError]   = useState("");

  const [form,         setForm]         = useState({ amount: "", dueExchange: "", date: "", name: "", phone: "", otherExchangeExpenses: "", remarks: "" });
  const [submitting,   setSubmitting]   = useState(false);
  const [formError,    setFormError]    = useState("");
  const [success,      setSuccess]      = useState(false);
  const [savedPayment, setSavedPayment] = useState(null);

  // ── fetch when modal opens ─────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !bike?._id) return;

    const load = async () => {
      try {
        setFetchLoading(true);
        setFetchError("");
        setSaleDetail(null);
        setSaleId(null);
        setSuccess(false);
        setFormError("");
        setSavedPayment(null);

        const res  = await api.get(`/specific-bike-details/${bike._id}`);
        const data = res.data?.data;

        // ✅ saleId from populated ExchangeSellId object
        const resolvedSaleId = data?.stockDetails?.ExchangeSellId?._id || null;
        setSaleId(resolvedSaleId);

        // ✅ exchange payment summary from exchangeSaleInfo
        const exchange = data?.exchangeSaleInfo?.exchange || null;
        const existing = exchange?.payments?.[0];

        setSaleDetail({
          exchangePrice:   exchange?.exchangePrice   || 0,
          paidAmount:      exchange?.paidAmount      || 0,
          remainingAmount: exchange?.remainingAmount || 0,
          extraAmount:     exchange?.extraAmount     || 0,
          status:          exchange?.status          || "",
          payments:        exchange?.payments        || [],
          dueExchange:     existing?.dueExchange     || null,
        });

        // ✅ auto-fill form from payments[0]
        setForm({
          amount: existing?.amount ? String(existing.amount) : "",
          dueExchange: existing?.dueExchange?.totalAmount !== undefined
            ? String(existing.dueExchange.totalAmount)
            : "",
          date:   existing?.date
            ? new Date(existing.date).toISOString().slice(0, 10)
            : new Date().toISOString().slice(0, 10),
          name:   existing?.name  || "",
          phone:  existing?.phone || "",
          otherExchangeExpenses: existing?.otherExchangeExpenses ? String(existing.otherExchangeExpenses) : "",
          remarks: existing?.remarks || "",
        });

      } catch {
        setFetchError("Failed to load exchange details. Please try again.");
      } finally {
        setFetchLoading(false);
      }
    };

    load();
  }, [open, bike]);

  // ── submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setFormError("");

    if (!form.amount || Number(form.amount) <= 0) { setFormError("Please enter a valid amount.");  return; }
    if (!form.date)                                { setFormError("Payment date is required.");     return; }
    if (!form.name.trim())                         { setFormError("Recipient name is required.");   return; }
    if (!form.phone.trim())                        { setFormError("Phone number is required.");     return; }
    if (form.otherExchangeExpenses !== "" && Number(form.otherExchangeExpenses) < 0) {
      setFormError("Other exchange expenses cannot be negative."); return;
    }
    if (form.dueExchange !== "" && Number(form.dueExchange) < 0) {
      setFormError("Due exchange amount cannot be negative."); return;
    }
    
    const alreadyDue = saleDetail?.dueExchange?.paidAmount || 0;
    if (form.dueExchange !== "" && Number(form.dueExchange) < alreadyDue) {
      setFormError(`Due amount cannot be less than due payments already made (${money(alreadyDue)}).`); return;
    }

    try {
      setSubmitting(true);
      const res = await api.put(`/update-sale-exchange-payment/${saleId}`, {
        amount: Number(form.amount),
        dueExchange: form.dueExchange === "" ? undefined : Number(form.dueExchange),
        date:   form.date,
        name:   form.name.trim(),
        phone:  form.phone.trim(),
        otherExchangeExpenses: form.otherExchangeExpenses === "" ? 0 : Number(form.otherExchangeExpenses),
        remarks: form.remarks.trim(),
      });
      setSavedPayment(res.data?.data || null);
      setSuccess(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setFormError(err?.response?.data?.message || "Update failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => { if (!submitting) onClose(); };

  const existingPayment = saleDetail?.payments?.[0] || null;

  // live hint
  const afterPay = saleDetail?.exchangePrice && form.amount
    ? saleDetail.exchangePrice - Number(form.amount)
    : null;

  const titleText = success
    ? "Payment Updated!"
    : existingPayment
    ? "Update Exchange Payment"
    : "Record Exchange Payment";

  const titleBg = success
    ? "linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)"
    : "linear-gradient(135deg, #bf360c 0%, #e65100 100%)";

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open} onClose={handleClose} fullWidth maxWidth="xs"
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      {/* ── TITLE ── */}
      <DialogTitle sx={{
        fontWeight: 800, background: titleBg, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        py: 1.8, px: 2.5,
      }}>
        <Box display="flex" alignItems="center" gap={1}>
          {success
            ? <CheckCircleIcon sx={{ fontSize: 20 }} />
            : existingPayment
            ? <EditIcon        sx={{ fontSize: 20 }} />
            : <PaymentIcon     sx={{ fontSize: 20 }} />}
          <Typography fontWeight={800} fontSize="1rem">{titleText}</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: "#fff" }} disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>

        {/* ── LOADING ── */}
        {fetchLoading && (
          <Box textAlign="center" py={6}>
            <CircularProgress size={32} sx={{ color: "#e65100" }} />
            <Typography variant="caption" display="block" mt={1.5} color="text.secondary">
              Loading exchange details…
            </Typography>
          </Box>
        )}

        {/* ── FETCH ERROR ── */}
        {!fetchLoading && fetchError && (
          <Box>
            <Alert severity="error" sx={{ borderRadius: 2, mb: 2 }}>{fetchError}</Alert>
            <Button fullWidth variant="outlined" onClick={handleClose} sx={{ borderRadius: 2 }}>
              Close
            </Button>
          </Box>
        )}

        {!fetchLoading && !fetchError && (
          <>
            {/* ── Bike card ── */}
            <Paper elevation={0} sx={{
              p: 1.8, mb: 2, borderRadius: 2,
              border: "1px solid #e0e7ef",
              background: "linear-gradient(135deg, #fff8f5 0%, #fff 100%)",
            }}>
              <Box display="flex" alignItems="center" gap={1} mb={1.2}>
                <TwoWheelerIcon sx={{ color: "#e65100", fontSize: 17 }} />
                <Typography fontWeight={800} fontSize="0.78rem" color="#e65100" letterSpacing={0.3}>
                  Bike Details
                </Typography>
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={6}><Label>Model</Label><Val>{bike?.model}</Val></Grid>
                <Grid item xs={6}><Label>Reg. Number</Label><Val>{bike?.registrationNumber}</Val></Grid>
                <Grid item xs={6}><Label>Owner</Label><Val>{bike?.ownerName}</Val></Grid>
                <Grid item xs={6}><Label>Mobile</Label><Val>{bike?.mobile}</Val></Grid>
                <Grid item xs={6}>
                  <Label>Purchase Price</Label>
                  <Val color="#2e7d32">{bike?.purchasePrice ? money(bike.purchasePrice) : "—"}</Val>
                </Grid>
              </Grid>
            </Paper>

            {/* ── Exchange price + summary banner ── */}
            {saleDetail && (
              <Paper elevation={0} sx={{
                p: 1.8, mb: 2, borderRadius: 2,
                bgcolor: "#fff3e0", border: "1px solid #ffb74d", textAlign: "center",
              }}>
                <Label>Exchange Price (Agreed Value)</Label>
                <Typography variant="h5" fontWeight={900} color="#e65100" mt={0.5}>
                  {money(saleDetail.exchangePrice)}
                </Typography>
                <Grid container spacing={1} mt={0.5} textAlign="left">
                  <Grid item xs={4}>
                    <Label>Total</Label>
                    <Val color="#333">{money(saleDetail.exchangePrice)}</Val>
                  </Grid>
                  <Grid item xs={4}>
                    <Label>Paid</Label>
                    <Val color="#2e7d32">{money(saleDetail.paidAmount)}</Val>
                  </Grid>
                  <Grid item xs={4}>
                    <Label>Remaining</Label>
                    <Val color={saleDetail.remainingAmount > 0 ? "#c62828" : "#2e7d32"}>
                      {money(saleDetail.remainingAmount)}
                    </Val>
                  </Grid>
                  {saleDetail.extraAmount > 0 && (
                    <Grid item xs={12}>
                      <Label>Extra Paid</Label>
                      <Val color="#6a1b9a">{money(saleDetail.extraAmount)}</Val>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}

            {/* ══ SUCCESS ══════════════════════════════════════════════════════ */}
            {success && (
              <>
                <Paper elevation={0} sx={{
                  p: 2.5, mb: 2, borderRadius: 2, textAlign: "center",
                  bgcolor: "#e8f5e9", border: "1px solid #a5d6a7",
                }}>
                  <CheckCircleIcon sx={{ color: "#2e7d32", fontSize: 44 }} />
                  <Typography fontWeight={800} color="#2e7d32" mt={0.8} fontSize="1rem">
                    Payment Updated!
                  </Typography>
                  <Typography variant="caption" color="#555">
                    Exchange payment has been updated successfully.
                  </Typography>
                </Paper>

                {savedPayment && (
                  <Paper elevation={0} sx={{
                    p: 2, mb: 2, borderRadius: 2,
                    border: "1px solid #dcedc8", bgcolor: "#f9fbe7",
                  }}>
                    <Box display="flex" alignItems="center" gap={0.8} mb={1.2}>
                      <ReceiptIcon sx={{ color: "#2e7d32", fontSize: 14 }} />
                      <Typography variant="caption" fontWeight={700} color="#2e7d32"
                        letterSpacing={0.5} textTransform="uppercase">
                        Updated Receipt
                      </Typography>
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Label>Exchange Price</Label>
                        <Val color="#333">{money(savedPayment.exchangePrice)}</Val>
                      </Grid>
                      <Grid item xs={6}>
                        <Label>Amount Paid</Label>
                        <Val color="#2e7d32">{money(savedPayment.paidAmount)}</Val>
                      </Grid>
                      <Grid item xs={6}>
                        <Label>Remaining</Label>
                        <Val color={savedPayment.remainingAmount > 0 ? "#c62828" : "#2e7d32"}>
                          {money(savedPayment.remainingAmount)}
                        </Val>
                      </Grid>
                      <Grid item xs={6}>
                        <Label>Status</Label>
                        <Chip label={savedPayment.status} size="small" sx={{
                          mt: 0.3, fontWeight: 800, fontSize: "0.65rem",
                          bgcolor: savedPayment.status === "PAID" ? "#e8f5e9" : "#fff3e0",
                          color:   savedPayment.status === "PAID" ? "#2e7d32" : "#e65100",
                          border:  `1px solid ${savedPayment.status === "PAID" ? "#a5d6a7" : "#ffb74d"}`,
                        }} />
                      </Grid>
                      {savedPayment.invoiceNo && (
                        <Grid item xs={12}><Label>Invoice</Label><Val>{savedPayment.invoiceNo}</Val></Grid>
                      )}
                      {savedPayment.payment && (
                        <>
                          <Grid item xs={6}><Label>Recipient</Label><Val>{savedPayment.payment.name}</Val></Grid>
                          <Grid item xs={6}><Label>Phone</Label><Val>{savedPayment.payment.phone}</Val></Grid>
                          <Grid item xs={6}>
                            <Label>Payment Date</Label>
                            <Val>
                              {savedPayment.payment.date
                                ? new Date(savedPayment.payment.date).toLocaleDateString("en-IN")
                                : "—"}
                            </Val>
                          </Grid>
                          {savedPayment.payment.otherExchangeExpenses > 0 && (
                            <Grid item xs={6}>
                              <Label>Other Expenses</Label>
                              <Val>{money(savedPayment.payment.otherExchangeExpenses)}</Val>
                            </Grid>
                          )}
                          {savedPayment.payment.remarks && (
                            <Grid item xs={12}><Label>Remarks</Label><Val>{savedPayment.payment.remarks}</Val></Grid>
                          )}
                        </>
                      )}
                    </Grid>
                  </Paper>
                )}

                {savedPayment?.dueExchange && <DueSummary due={savedPayment.dueExchange} />}

                <Button fullWidth variant="contained" onClick={handleClose}
                  sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}>
                  Done
                </Button>
              </>
            )}

            {/* ══ FORM (new OR update) ═════════════════════════════════════════ */}
            {!success && (
              <>
                {/* Current payment record if exists */}
                {existingPayment && (
                  <>
                    <Box display="flex" alignItems="center" gap={0.8} mb={1}>
                      <ReceiptIcon sx={{ color: "#888", fontSize: 14 }} />
                      <Typography variant="caption" fontWeight={700} color="#888"
                        letterSpacing={0.5} textTransform="uppercase">
                        Current Payment Record
                      </Typography>
                    </Box>
                    <Paper elevation={0} sx={{
                      p: 1.8, mb: 2, borderRadius: 2,
                      border: "1px solid #dcedc8", bgcolor: "#f9fbe7",
                    }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Label>Amount</Label>
                          <Typography variant="body1" fontWeight={900} color="#2e7d32">
                            {money(existingPayment.amount)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Label>Date</Label>
                          <Val>
                            {existingPayment.date
                              ? new Date(existingPayment.date).toLocaleDateString("en-IN")
                              : "—"}
                          </Val>
                        </Grid>
                        {existingPayment.name  && <Grid item xs={6}><Label>Recipient</Label><Val>{existingPayment.name}</Val></Grid>}
                        {existingPayment.phone && <Grid item xs={6}><Label>Phone</Label><Val>{existingPayment.phone}</Val></Grid>}
                        {existingPayment.otherExchangeExpenses > 0 && (
                          <Grid item xs={6}>
                            <Label>Other Expenses</Label>
                            <Val>{money(existingPayment.otherExchangeExpenses)}</Val>
                          </Grid>
                        )}
                        {existingPayment.remarks && (
                          <Grid item xs={12}><Label>Remarks</Label><Val>{existingPayment.remarks}</Val></Grid>
                        )}
                      </Grid>
                    </Paper>

                    {/* due summary + due payment history */}
                    {saleDetail?.dueExchange && <DueSummary due={saleDetail.dueExchange} />}

                    <Alert severity="warning" sx={{ mb: 2, borderRadius: 2, fontSize: "0.74rem", py: 0.5 }}>
                      ⚠️ Submitting will <strong>overwrite</strong> the first payment record. Due part payments already added are kept.
                    </Alert>
                  </>
                )}

                <Divider sx={{ mb: 2 }}>
                  <Typography variant="caption" fontWeight={700} color="#888" letterSpacing={0.5}>
                    {existingPayment ? "UPDATE PAYMENT" : "PAYMENT DETAILS"}
                  </Typography>
                </Divider>

                {/* ── Form fields — pre-filled from payments[0] ── */}
                <Box display="flex" flexDirection="column" gap={1.8}>
                  <TextField
                    label="Payment Amount (₹)" type="number" size="small" fullWidth
                    value={form.amount}
                    onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))}
                    inputProps={{ min: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon sx={{ fontSize: 15, color: "#e65100" }} />
                        </InputAdornment>
                      )
                    }}
                    helperText={
                      afterPay === null
                        ? (saleDetail?.exchangePrice ? `Agreed: ${money(saleDetail.exchangePrice)}` : undefined)
                        : afterPay > 0
                          ? `Due after this payment: ${money(afterPay)}`
                          : afterPay < 0
                            ? `Extra over agreed value: ${money(-afterPay)}`
                            : "Fully paid with this payment"
                    }
                  />
                  {/* 🆕 due exchange amount */}
                  <TextField
                    label="Due Exchange Amount (₹) (optional)" type="number" size="small" fullWidth
                    value={form.dueExchange}
                    onChange={(e) => setForm((p) => ({ ...p, dueExchange: e.target.value }))}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon sx={{ fontSize: 15, color: "#c62828" }} />
                        </InputAdornment>
                      )
                    }}
                    helperText={
                      saleDetail?.dueExchange?.paidAmount > 0
                        ? `Already paid on due: ${money(saleDetail.dueExchange.paidAmount)} — cannot go below this`
                        : "Amount still owed to the exchange customer"
                    }
                  />
                  <TextField
                    label="Payment Date" type="date" size="small" fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={form.date}
                    onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon sx={{ fontSize: 14, color: "#666" }} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    label="Recipient Name" size="small" fullWidth
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonIcon sx={{ fontSize: 15, color: "#666" }} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    label="Phone Number" size="small" fullWidth
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PhoneIcon sx={{ fontSize: 15, color: "#666" }} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    label="Other Exchange Expenses (optional)" type="number" size="small" fullWidth
                    value={form.otherExchangeExpenses}
                    onChange={(e) => setForm((p) => ({ ...p, otherExchangeExpenses: e.target.value }))}
                    inputProps={{ min: 0 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CurrencyRupeeIcon sx={{ fontSize: 15, color: "#666" }} />
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    label="Remarks (optional)" size="small" fullWidth multiline rows={2}
                    value={form.remarks}
                    onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <NotesIcon sx={{ fontSize: 15, color: "#666" }} />
                        </InputAdornment>
                      )
                    }}
                  />
                </Box>

                {formError && (
                  <Alert severity="error" sx={{ mt: 2, borderRadius: 2, fontSize: "0.8rem" }}>
                    {formError}
                  </Alert>
                )}

                <Box display="flex" gap={1.5} mt={2.5}>
                  <Button fullWidth variant="outlined" onClick={handleClose} disabled={submitting}
                    sx={{ borderRadius: 2, fontWeight: 700 }}>
                    Cancel
                  </Button>
                  <Button fullWidth variant="contained" onClick={handleSubmit} disabled={submitting}
                    sx={{ borderRadius: 2, fontWeight: 700, bgcolor: "#e65100", "&:hover": { bgcolor: "#bf360c" } }}>
                    {submitting ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={14} sx={{ color: "#fff" }} />
                        Saving…
                      </Box>
                    ) : existingPayment ? "✅ Update Payment" : "✅ Record Payment"}
                  </Button>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UpdateExchangePaymentModal;